import { type DeepPartial } from 'react-hook-form';

import { SELECTED_BANK_STATUSES } from '@/constants';
import {
  generateRandomSixDiginCode,
  keysToCamelCase,
  validateNumberInputValue
} from '@/services';
import { formatPhoneNumber } from '@/services/formatter';
import {
  type TDataSource,
  type TDataSourceOnline,
  type TDataSourceResponses
} from '@/types/dataSource';
import {
  type TAutoCredLeasingResponse,
  type TAutoCredLeasingResponseApi
} from '@/types/newOrder';
import {
  type TAutoCredListItemLeasingResponse,
  type TAutoCredListItemLeasingResponseApi,
  type TOnlineRequest,
  type TRequest,
  type TRequestDetailed,
  type TResponse
} from '@/types/request';

import { getUserFullName } from '../helpers/strings';

type TOffersMapKeys =
  | 'approved'
  | 'approvedAlternatives'
  | 'clientAccepted'
  | 'denied'
  | 'error'
  | 'financed'
  | 'waiting'
  | 'waitingFinancing';

type TLeasingOffersMapKeys =
  | 'approved'
  | 'approvedAlternatives'
  | 'denied'
  | 'error'
  | 'financed'
  | 'waiting'
  | 'waitingFinancing';

export const autoCredMapper = (
  data:
    | {
        requests: TRequest[];
        isDetailed: false;
      }
    | {
        requests: TRequestDetailed[];
        isDetailed: true;
      }
): TDataSource[] => {
  const { requests, isDetailed } = data;
  if (!Array.isArray(requests) || requests.length === 0) return [];

  return requests.map((request) => {
    const requestCode = request.code;
    const clientFullName = getUserFullName(request.client, {
      defaultValues: { firstName: '-', lastName: '-', middleName: '-' }
    });
    const telNumber =
      request?.client?.phone_number?.length === 11
        ? request?.client?.phone_number || ''
        : `7${request?.client?.phone_number || ''}`;
    const amount = request?.vehicle_cost || '';
    const auto = request?.model
      ? `${request?.model?.brand?.name || ''} ${
          request?.model?.name
        }, ${request?.car_year || ''}`
      : request.used_car
        ? `${request.used_car.brand_name}, ${request.used_car.model_name}, ${request.used_car.year}`
        : '';
    const autoType = request?.auto_type?.name || '';
    const autoDescription = `${request?.model?.brand?.name || ''} ${
      request?.model?.name
    }, ${request?.model?.year || ''} `;
    const date = request?.create_date || '';
    const isUnverified = request?.status.code === 'sms_verificating';
    const author = getUserFullName(request.user, {
      defaultValues: { firstName: '-', lastName: '-', middleName: '-' }
    });

    const offersMap: Record<TOffersMapKeys, TResponse[]> = {
      approved: [],
      approvedAlternatives: [],
      clientAccepted: [],
      denied: [],
      error: [],
      financed: [],
      waiting: [],
      waitingFinancing: []
    };

    const leasingOffersMap: Record<
      TLeasingOffersMapKeys,
      TAutoCredListItemLeasingResponse[]
    > = {
      approved: [],
      approvedAlternatives: [],
      denied: [],
      error: [],
      financed: [],
      waiting: [],
      waitingFinancing: []
    };

    request.Responses.forEach((response) => {
      const statusCode = response?.status?.code;

      if (!statusCode) return;

      if (
        SELECTED_BANK_STATUSES.includes(statusCode) &&
        statusCode !== 'financed'
      ) {
        offersMap.waitingFinancing.push(response);
        return;
      }

      switch (statusCode) {
        case 'approved':
        case 'pre_approved':
          offersMap.approved.push(response);
          break;
        case 'accepted_alternative':
          offersMap.approvedAlternatives.push(response);
          break;
        case 'client_accepted':
          offersMap.approved.push(response);
          offersMap.clientAccepted.push(response);
          break;
        case 'cancelled':
        case 'declined':
          offersMap.denied.push(response);
          break;
        case 'error':
        case 'bank_response_timeout':
          offersMap.error.push(response);
          break;
        case 'financed':
          offersMap.financed.push(response);
          break;
        case 'on_rework':
        case 'on_verification':
        case 'waiting_bank_response':
          offersMap.waiting.push(response);
          break;
        default:
          break;
      }
    });

    if (!checkIsDetailed(request)) {
      request.Leasings?.[0]?.responses
        .map(parseAutoCredListItemLeasingResponse)
        .forEach((leasingResponse) => {
          const statusCode = leasingResponse?.status?.code;

          if (!statusCode) return;

          switch (statusCode) {
            case 'approved':
            case 'client_accepted':
              leasingOffersMap.approved.push(leasingResponse);
              break;
            case 'accepted_alternative':
              leasingOffersMap.approvedAlternatives.push(leasingResponse);
              break;
            case 'cancelled':
            case 'declined':
              leasingOffersMap.denied.push(leasingResponse);
              break;
            case 'error':
            case 'bank_response_timeout':
              leasingOffersMap.error.push(leasingResponse);
              break;
            case 'financed':
              leasingOffersMap.financed.push(leasingResponse);
              break;
            case 'waiting_bank_response':
              leasingOffersMap.waiting.push(leasingResponse);
              break;
            case 'waiting_of_financing':
              leasingOffersMap.waitingFinancing.push(leasingResponse);
              break;
            default:
              break;
          }
        });
    }

    const isFinanced = offersMap.financed.length > 0;
    const clientAccepted = offersMap.clientAccepted.length > 0;
    const isSelected = offersMap.waitingFinancing.length > 0;
    const selectedBank = clientAccepted
      ? offersMap.clientAccepted[0]?.bank?.name || ''
      : isFinanced
        ? offersMap.financed[0]?.bank?.name || ''
        : offersMap.waitingFinancing.length
          ? offersMap.waitingFinancing[0]?.bank?.name || ''
          : '';

    const isFinancedLeasing = leasingOffersMap.financed.length > 0;
    const isSelectedLeasing = leasingOffersMap.waitingFinancing.length > 0;

    const isRequestInDraft = request.status.code === 'draft';
    const isAddedToQueue = request.status.code === 'add_queue';
    const isError = request.status.code === 'error';

    const responsesAmount =
      offersMap.financed.length +
      offersMap.error.length +
      offersMap.waitingFinancing.length +
      offersMap.waiting.length +
      offersMap.denied.length +
      selectedBank.length +
      offersMap.approved.length +
      leasingOffersMap.financed.length +
      leasingOffersMap.error.length +
      leasingOffersMap.waitingFinancing.length +
      leasingOffersMap.waiting.length +
      leasingOffersMap.denied.length +
      leasingOffersMap.approved.length;

    let selectedLeasing: TAutoCredListItemLeasingResponse | null = null;
    if (isFinancedLeasing) {
      selectedLeasing = leasingOffersMap.financed[0];
    } else if (leasingOffersMap.waitingFinancing.length) {
      selectedLeasing = leasingOffersMap.waitingFinancing[0];
    }

    function checkIsDetailed(
      targetRequest: typeof request
    ): targetRequest is TRequestDetailed {
      return !!targetRequest && isDetailed;
    }

    return {
      client: {
        code: requestCode,
        fullName: clientFullName,
        iin: request?.client?.client_iin,
        telNumber: formatPhoneNumber(telNumber)
      },
      createdUser: getUserFullName(request.user),
      created_at: {
        author: author,
        author_uuid: request?.user_uuid,
        date: date
      },
      sentTo:
        request.sent_to ||
        (request.is_online_request ? 'banks' : 'banks_leasing'),
      dealer: {
        city_uuid: request?.dealer?.city_uuid || '',
        code: request?.dealer?.code,
        name: request?.dealer?.name || '',
        uuid: request?.dealer?.uuid || ''
      },
      id: request.uuid,
      isOnlineRequest: !!request.is_online_request,
      loan: {
        amount: validateNumberInputValue(amount, 10, true),
        auto: auto,
        dealer: request?.dealer?.name || '',
        financed_date: offersMap.financed[0]?.funded_date,
        initialFee:
          validateNumberInputValue(request?.init_fee?.toString(), 10, true) ||
          '',
        loanTerm: request?.loan_period_code
      },
      orderType: {
        auto: autoDescription,
        autoType: autoType,
        code: requestCode ? requestCode : generateRandomSixDiginCode()
      },
      takenUserFullname: request.Online_Client_Requests?.[0]
        ? checkIsDetailed(request)
          ? getUserFullName(request.Online_Client_Requests[0].taken_by_user)
          : getUserFullName(request.Online_Client_Requests[0].taken_by_user)
        : null,
      responses:
        request.Responses.length > 0 &&
        (request.sent_to === 'banks' || request.sent_to === 'banks_leasing')
          ? request.Responses.map((response) => {
              const responseData: TDataSourceResponses = {
                bank: response?.bank?.name,
                createDate: response.create_date,
                funded_date: response.funded_date,
                id: response.uuid,
                responseDate: response?.response_date || response.create_date,
                status: response?.status?.code
              };
              return responseData;
            })
          : [],
      leasingResponses:
        (!checkIsDetailed(request) &&
        (request.sent_to === 'leasing' || request.sent_to === 'banks_leasing')
          ? request.Leasings?.[0]?.responses.map(
              parseAutoCredListItemLeasingResponse
            )
          : []) || [],
      responsesAmount: responsesAmount,
      status: {
        approved: mapResponsesToDataSourceResponses(offersMap.approved),
        approvedAlternatives: mapResponsesToDataSourceResponses(
          offersMap.approvedAlternatives
        ),
        clientAccepted: clientAccepted,
        code: request?.status?.code,
        denied: mapResponsesToDataSourceResponses(offersMap.denied),
        errors: mapResponsesToDataSourceResponses(offersMap.error),
        expectations: mapResponsesToDataSourceResponses(offersMap.waiting),
        isAddedToQueue: isAddedToQueue,
        isDraft: isRequestInDraft,
        isError,
        isFinanced: isFinanced,
        isNotTakenOnline:
          !!request.is_online_request &&
          (request.Online_Client_Requests?.length ?? 0) > 0 &&
          (checkIsDetailed(request)
            ? !request.Online_Client_Requests?.[0]?.taken_by_user
            : !request.Online_Client_Requests?.[0]?.taken_by_user?.uuid),
        isSelected: isSelected,
        isUnverified,
        name: request?.status?.name,
        leasingStatus: {
          approved: leasingOffersMap.approved,
          approvedAlternatives: leasingOffersMap.approvedAlternatives,
          denied: leasingOffersMap.denied,
          errors: leasingOffersMap.error,
          expectations: leasingOffersMap.waiting,
          isFinanced: isFinancedLeasing,
          isSelected: isSelectedLeasing,
          selectedLeasing: selectedLeasing
        },
        selectedBank: selectedBank
      }
    } satisfies TDataSource;
  });
};

export const autoCredOnlineMapper = (
  data: DeepPartial<TOnlineRequest>[]
): TDataSourceOnline[] => {
  if (!Array.isArray(data) || data.length === 0) return [];

  return data.map((request) => {
    const { primary_request } = request;

    const clientFullname = getUserFullName(primary_request?.client || {}, {
      defaultValues: { firstName: '-', lastName: '-', middleName: '-' }
    });
    const telNumber = formatPhoneNumber(
      request?.phone_number?.length === 11
        ? request.phone_number || ''
        : `7${request.phone_number || ''}`
    );
    const isUnverified = request?.status?.code === 'sms_verificating';
    const auto = `${request?.equipment?.model?.brand?.name || ''} ${
      request?.equipment?.model?.name
    }, ${request?.year || ''}`;

    const offersMap: Record<
      TOffersMapKeys,
      Exclude<
        Exclude<typeof primary_request, undefined>['Responses'],
        undefined
      >
    > = {
      approved: [],
      approvedAlternatives: [],
      clientAccepted: [],
      denied: [],
      error: [],
      financed: [],
      waiting: [],
      waitingFinancing: []
    };

    primary_request?.Responses?.forEach((response) => {
      const statusCode = response?.status?.code;

      if (!statusCode) return;

      if (
        SELECTED_BANK_STATUSES.includes(statusCode) &&
        statusCode !== 'financed'
      ) {
        offersMap.waitingFinancing.push(response);
        return;
      }

      switch (statusCode) {
        case 'approved':
        case 'pre_approved':
          offersMap.approved.push(response);
          break;
        case 'accepted_alternative':
          offersMap.approvedAlternatives.push(response);
          break;
        case 'client_accepted':
          offersMap.approved.push(response);
          offersMap.clientAccepted.push(response);
          break;
        case 'cancelled':
        case 'declined':
          offersMap.denied.push(response);
          break;
        case 'error':
          offersMap.error.push(response);
          break;
        case 'financed':
          offersMap.financed.push(response);
          break;
        case 'on_rework':
        case 'on_verification':
        case 'waiting_bank_response':
          offersMap.waiting.push(response);
          break;
        default:
          break;
      }
    });

    const isFinanced = offersMap.financed.length > 0;
    const clientAccepted = offersMap.clientAccepted.length > 0;
    const isSelected = offersMap.waitingFinancing.length > 0;
    const selectedBank = clientAccepted
      ? offersMap.clientAccepted[0]?.bank?.name || ''
      : isFinanced
        ? offersMap.financed[0]?.bank?.name || ''
        : offersMap.waitingFinancing.length
          ? offersMap.waitingFinancing[0]?.bank?.name || ''
          : '';

    const isRequestInDraft = request?.status?.code === 'draft';
    const isAddedToQueue = request?.status?.code === 'add_queue';
    const isError = request?.status?.code === 'error';

    const responsesAmount =
      offersMap.financed.length +
      offersMap.error.length +
      offersMap.waitingFinancing.length +
      offersMap.waiting.length +
      offersMap.denied.length +
      selectedBank.length +
      offersMap.approved.length;

    // Преобразуем массивы ответов для онлайн заявок
    const approvedResponses =
      primary_request?.Responses?.filter(
        (response) =>
          response?.status?.code === 'approved' ||
          response?.status?.code === 'pre_approved'
      ).map((response) => ({
        ...response,
        bank: {
          code: response?.bank?.code || '',
          fillname: response?.bank?.fillname || '',
          name: response?.bank?.name || '',
          uuid: response?.bank?.uuid || ''
        },
        fundedDate: response?.funded_date || '',
        status: {
          code: response?.status?.code || '',
          name: response?.status?.name || '',
          uuid: response?.status?.uuid || ''
        }
      })) || [];

    const approvedAlternativesResponses =
      primary_request?.Responses?.filter(
        (response) => response?.status?.code === 'accepted_alternative'
      ).map((response) => ({
        ...response,
        bank: {
          code: response?.bank?.code || '',
          fillname: response?.bank?.fillname || '',
          name: response?.bank?.name || '',
          uuid: response?.bank?.uuid || ''
        },
        fundedDate: response?.funded_date || '',
        status: {
          code: response?.status?.code || '',
          name: response?.status?.name || '',
          uuid: response?.status?.uuid || ''
        }
      })) || [];

    const deniedResponses =
      primary_request?.Responses?.filter(
        (response) =>
          response?.status?.code === 'cancelled' ||
          response?.status?.code === 'declined'
      ).map((response) => ({
        ...response,
        bank: {
          code: response?.bank?.code || '',
          fillname: response?.bank?.fillname || '',
          name: response?.bank?.name || '',
          uuid: response?.bank?.uuid || ''
        },
        fundedDate: response?.funded_date || '',
        status: {
          code: response?.status?.code || '',
          name: response?.status?.name || '',
          uuid: response?.status?.uuid || ''
        }
      })) || [];

    const errorResponses =
      primary_request?.Responses?.filter(
        (response) => response?.status?.code === 'error'
      ).map((response) => ({
        ...response,
        bank: {
          code: response?.bank?.code || '',
          fillname: response?.bank?.fillname || '',
          name: response?.bank?.name || '',
          uuid: response?.bank?.uuid || ''
        },
        fundedDate: response?.funded_date || '',
        status: {
          code: response?.status?.code || '',
          name: response?.status?.name || '',
          uuid: response?.status?.uuid || ''
        }
      })) || [];

    const waitingResponses =
      primary_request?.Responses?.filter(
        (response) =>
          response?.status?.code === 'on_rework' ||
          response?.status?.code === 'on_verification' ||
          response?.status?.code === 'waiting_bank_response'
      ).map((response) => ({
        ...response,
        bank: {
          code: response?.bank?.code || '',
          fillname: response?.bank?.fillname || '',
          name: response?.bank?.name || '',
          uuid: response?.bank?.uuid || ''
        },
        fundedDate: response?.funded_date || '',
        status: {
          code: response?.status?.code || '',
          name: response?.status?.name || '',
          uuid: response?.status?.uuid || ''
        }
      })) || [];

    return {
      client: {
        code: request.primary_request?.code || '',
        fullName: clientFullname,
        iin: request?.iin || '',
        telNumber
      },
      loan: {
        amount: validateNumberInputValue(
          String(request.equipment?.cost ?? ''),
          10,
          true
        ),
        auto,
        dealer: request?.dealer?.name || '',
        financedDate: offersMap.financed[0]?.funded_date ?? null,
        initialFee:
          validateNumberInputValue(
            (request?.initial_payment_amount ?? '').toString(),
            10,
            true
          ) || '',
        loanTerm: request?.loan_term || null
      },
      createdAt: request.created_date || '',
      dealerName: request?.dealer?.name || '',
      responses:
        primary_request?.Responses?.map((response) => ({
          ...response,
          bank: {
            code: response?.bank?.code || '',
            fillname: response?.bank?.fillname || '',
            name: response?.bank?.name || '',
            uuid: response?.bank?.uuid || ''
          },
          fundedDate: response?.funded_date || '',
          status: {
            code: response?.status?.code || '',
            name: response?.status?.name || '',
            uuid: response?.status?.uuid || ''
          }
        })) || [],
      responsesAmount: responsesAmount,
      fcStatus: {
        code: request?.fc_status?.code || '',
        name: request?.fc_status?.name || ''
      },
      primaryRequestUuid: request?.primary_request_uuid || '',
      takenAt: request.taken_at || '',
      uuid: request.uuid || '',
      status: {
        approved: approvedResponses,
        approvedAlternatives: approvedAlternativesResponses,
        clientAccepted: clientAccepted,
        code: request?.status?.code || '',
        denied: deniedResponses,
        errors: errorResponses,
        expectations: waitingResponses,
        isAddedToQueue: isAddedToQueue,
        isDraft: isRequestInDraft,
        isError,
        isFinanced: isFinanced,
        isSelected: isSelected,
        isUnverified,
        name: request?.status?.name || '',
        selectedBank: selectedBank
      }
    } satisfies TDataSourceOnline;
  });
};

const mapResponsesToDataSourceResponses = (
  responses: TResponse[]
): TDataSourceResponses[] => {
  return responses.map((response) => ({
    bank: response?.bank?.name,
    createDate: response.create_date,
    funded_date: response.funded_date,
    id: response.uuid,
    responseDate: response?.response_date || response.create_date,
    status: response?.status?.code
  }));
};

export const parseAutoCredLeasingResponse = (
  response: TAutoCredLeasingResponseApi
): TAutoCredLeasingResponse => {
  return keysToCamelCase(response);
};

export const parseAutoCredListItemLeasingResponse = (
  response: TAutoCredListItemLeasingResponseApi
): TAutoCredListItemLeasingResponse => {
  return keysToCamelCase(response);
};
